import {
  Typography as MuiTypography,
  DialogContent as MuiDialogContent,
  Box as MuiBox,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import styled from 'styled-components';
import DialogTitle from 'components/Dialogs/DialogTitle';
import QuestionnaireDialogItem from './QuestionnaireDialogItem';

import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { getMainComponent } from 'lib/utils/metricComponents';
import { Button } from 'components';

const QuestionnaireDetailsDialog = (props) => {
  const { close, back, initialValues, update, patientId } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    responses,
    questionnaire,
    metricDefinitionId,
    isLatestVersion,
  } = initialValues;

  const displayScoreStatuses = ['COMPLETED', 'AMENDED'];

  return (
    <>
      <DialogTitle
        titleButton={
          back && (
            <SMuiBox
              onClick={back}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <BackIcon fontSize="10" />
              <MuiTypography fontSize={14} align="center" margin="1rem 0.5rem">
                {t('Back')}
              </MuiTypography>
            </SMuiBox>
          )
        }
        onClose={close}
      />

      <MuiDialogContent>
        {!isLatestVersion && (
          <SBannerBox display="flex" mb={3} alignItems="center">
            <WarningIcon
              sx={{
                color: theme.palette.colors.red100,
                fontSize: 20,
                mr: 1,
              }}
            />
            <MuiTypography
              fontSize={14}
              fontWeight={500}
              color={theme.palette.colors.gray500}
            >
              {t(
                "You're viewing a previous version of the questionnaire response."
              )}
            </MuiTypography>
          </SBannerBox>
        )}
        <MuiTypography variant="h1" align="left">
          {`${t('Score')}: `}
          {!displayScoreStatuses.includes(responses?.status)
            ? t('Not completed yet')
            : responses?.mainScoreMetric?.metricDefinitionId ===
              metricDefinitionId
            ? getMainComponent(responses?.mainScoreMetric?.components)
                ?.quantityValue?.value || '-'
            : getMainComponent(
                responses?.subScoreMetrics?.find(
                  (subscore) =>
                    subscore?.metricDefinitionId === metricDefinitionId
                )?.components
              )?.quantityValue?.value || '-'}
        </MuiTypography>
        <MuiBox display={'flex'} mt={1} alignItems="center">
          <MuiTypography
            fontSize={14}
            fontWeight={500}
            mr={0.5}
            width="150px"
            color={theme.palette.colors.gray400}
          >
            {`${t('Formula')}: `}
          </MuiTypography>
          <MuiTypography fontSize={14} fontWeight={500}>
            {questionnaire?.mainScoreConfig?.metricDefinitionId ===
            metricDefinitionId
              ? questionnaire?.mainScoreConfig?.formula?.expression
                  ?.replaceAll('__RAW_VALUE', ` (${t('Raw value')})`)
                  ?.replaceAll('__SCORE', ` (${t('Score')})`) ?? '-'
              : questionnaire?.subScoreConfig
                  ?.find(
                    (subscore) =>
                      subscore?.metricDefinitionId === metricDefinitionId
                  )
                  ?.formula?.expression?.replaceAll(
                    '__RAW_VALUE',
                    ` (${t('Raw value')})`
                  )
                  ?.replaceAll('__SCORE', ` (${t('Score')})`) ?? '-'}
          </MuiTypography>
        </MuiBox>
        <MuiBox display={'flex'} mt={1} alignItems="center">
          <MuiTypography
            fontSize={14}
            fontWeight={500}
            mr={0.5}
            width="150px"
            color={theme.palette.colors.gray400}
          >
            {t('Terminology')}:
          </MuiTypography>
          <MuiTypography fontSize={14} fontWeight={500}>
            {questionnaire?.mainScoreConfig?.code?.coding?.[0]?.display || '-'}
          </MuiTypography>
        </MuiBox>
        <MuiBox display={'flex'} mt={1} alignItems="center">
          <MuiTypography
            fontSize={14}
            fontWeight={500}
            mr={0.5}
            width="150px"
            color={theme.palette.colors.gray400}
          >
            {t('Date')}:
          </MuiTypography>
          <MuiTypography fontSize={14} fontWeight={500}>
            {(() => {
              if (!displayScoreStatuses.includes(responses?.status)) {
                return t('Not completed yet');
              }

              let dateStr = null;
              if (
                responses?.mainScoreMetric?.metricDefinitionId ===
                metricDefinitionId
              ) {
                dateStr = responses?.mainScoreMetric?.createdAt;
              } else {
                dateStr = responses?.subScoreMetrics?.find(
                  (subscore) =>
                    subscore?.metricDefinitionId === metricDefinitionId
                )?.createdAt;
              }

              if (!dateStr) {
                return '-';
              }

              try {
                const parsedDate = parseISO(dateStr);
                return (
                  t(format(parsedDate, 'MMMM')).slice(0, 3) +
                  format(parsedDate, ' dd, yyy')
                );
              } catch {
                return '-';
              }
            })()}
          </MuiTypography>
        </MuiBox>

        <MuiBox marginTop={4}>
          {responses?.questionStates.map((item, i) => (
            <QuestionnaireDialogItem
              key={item.questionId}
              state={item}
              response={responses?.questionResponses?.find(
                (q) => q.questionId === item.questionId
              )}
              question={questionnaire?.questions?.find(
                (q) => q.id === item.questionId
              )}
              index={i}
            />
          ))}
        </MuiBox>

        {isLatestVersion && patientId == responses?.patientId && (
          <MuiBox>
            <Button
              color="primary"
              onClick={update}
              title={t('Update Answers')}
            ></Button>
          </MuiBox>
        )}
      </MuiDialogContent>
    </>
  );
};

export default QuestionnaireDetailsDialog;

const SMuiBox = styled(MuiBox)`
  && {
    cursor: pointer;
  }
`;

const SBannerBox = styled(MuiBox)`
  && {
    background-color: #fff5f5;
    border-radius: 8px;
    padding: 12px 16px;
  }
`;
